// src/toolkitServices/diamondApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const diamondApi = createApi({
  reducerPath: "diamondApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["Diamond"],
  endpoints: (builder) => ({
    /* ---------------- CREATE ---------------- */
    // POST /diamonds/create
    addDiamond: builder.mutation({
      query: (body) => ({
        url: `/diamonds/create`,
        method: "POST",
        body,
      }),
      async onQueryStarted(_body, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          const created = res?.data;
          if (!created) return;

          // Inject on page=1 lists (if any cached)
          const argsList = diamondApi.util.selectCachedArgsForQuery(
            getState(),
            "getAllDiamonds"
          );
          for (const args of argsList) {
            if ((args?.page ?? 1) !== 1) continue;
            dispatch(
              diamondApi.util.updateQueryData("getAllDiamonds", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const exists = draft.data.some((x) => String(x._id) === String(created._id));
                if (!exists) {
                  draft.data.unshift(created);
                  // clamp page length if limit present
                  const lim = Number(args?.limit || 0);
                  if (lim && draft.data.length > lim) draft.data.pop();
                  if (typeof draft.total === "number") {
                    draft.total += 1;
                    draft.pages = Math.max(1, Math.ceil(draft.total / (draft.limit || lim || 10)));
                  }
                }
              })
            );
          }
        } catch {}
      },
    }),

    /* ---------------- LIST ---------------- */
    // GET /diamonds?page=&limit=&q=&activeOnly=&deductable=&shape=&color=&clarity=
    getAllDiamonds: builder.query({
      query: (params = {}) => {
        const usp = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "") usp.set(k, v);
        });
        const qs = usp.toString();
        return `/diamonds${qs ? `?${qs}` : ""}`;
      },
      providesTags: (res) =>
        res?.data
          ? [
              ...res.data.map((r) => ({ type: "Diamond", id: r._id })),
              { type: "Diamond", id: "LIST" },
            ]
          : [{ type: "Diamond", id: "LIST" }],
      keepUnusedDataFor: 60,
    }),

    /* ---------------- DETAIL ---------------- */
    // GET /diamonds/:id
    getDiamondById: builder.query({
      query: (id) => `/diamonds/${id}`,
      providesTags: (_res, _err, id) => [{ type: "Diamond", id }],
      keepUnusedDataFor: 120,
    }),

    /* ---------------- UPDATE ---------------- */
    // PUT /diamonds/:id
    updateDiamond: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/diamonds/${id}`,
        method: "PUT",
        body,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: res } = await queryFulfilled;
          const upd = res?.data;
          if (!upd) return;

          // patch detail
          dispatch(
            diamondApi.util.updateQueryData("getDiamondById", id, (draft) => {
              if (draft?.data) Object.assign(draft.data, upd);
            })
          );

          // patch in all cached lists
          dispatch(
            diamondApi.util.updateQueryData("getAllDiamonds", undefined, () => {})
          ); // no-op to ensure selector available
          // we can’t iterate args directly without selectCachedArgsForQuery:
        } catch {}
      },
      invalidatesTags: (_res, _err, { id }) => [{ type: "Diamond", id }, { type: "Diamond", id: "LIST" }],
    }),

    /* ---------------- DELETE ---------------- */
    // DELETE /diamonds/:id
    deleteDiamond: builder.mutation({
      query: (id) => ({ url: `/diamonds/${id}`, method: "DELETE" }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const argsList = diamondApi.util.selectCachedArgsForQuery(
          getState(),
          "getAllDiamonds"
        );
        for (const args of argsList) {
          patches.push(
            dispatch(
              diamondApi.util.updateQueryData("getAllDiamonds", args, (draft) => {
                if (!Array.isArray(draft?.data)) return;
                const before = draft.data.length;
                draft.data = draft.data.filter((x) => String(x._id) !== String(id));
                const removed = before - draft.data.length;
                if (removed > 0 && typeof draft.total === "number") {
                  draft.total = Math.max(0, draft.total - removed);
                  draft.pages = Math.max(
                    1,
                    Math.ceil(draft.total / (draft.limit || args?.limit || 10))
                  );
                }
              })
            )
          );
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
      invalidatesTags: (_res, _err, id) => [{ type: "Diamond", id }],
    }),

    /* ---------------- ACTIVATE / DEACTIVATE ---------------- */
    // PATCH /diamonds/:id/activate
    activateDiamond: builder.mutation({
      query: (id) => ({ url: `/diamonds/${id}/activate`, method: "PATCH" }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        // optimistic flip true in lists & detail
        const patches = [];

        const argsList = diamondApi.util.selectCachedArgsForQuery(
          getState(),
          "getAllDiamonds"
        );
        for (const args of argsList) {
          patches.push(
            dispatch(
              diamondApi.util.updateQueryData("getAllDiamonds", args, (draft) => {
                const it = draft?.data?.find?.((x) => String(x._id) === String(id));
                if (it) it.isActive = true;
              })
            )
          );
        }
        patches.push(
          dispatch(
            diamondApi.util.updateQueryData("getDiamondById", id, (draft) => {
              if (draft?.data) draft.data.isActive = true;
            })
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
      invalidatesTags: (_res, _err, id) => [{ type: "Diamond", id }],
    }),

    // PATCH /diamonds/:id/deactivate
    deactivateDiamond: builder.mutation({
      query: (id) => ({ url: `/diamonds/${id}/deactivate`, method: "PATCH" }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        // optimistic flip false in lists & detail
        const patches = [];

        const argsList = diamondApi.util.selectCachedArgsForQuery(
          getState(),
          "getAllDiamonds"
        );
        for (const args of argsList) {
          patches.push(
            dispatch(
              diamondApi.util.updateQueryData("getAllDiamonds", args, (draft) => {
                const it = draft?.data?.find?.((x) => String(x._id) === String(id));
                if (it) it.isActive = false;
              })
            )
          );
        }
        patches.push(
          dispatch(
            diamondApi.util.updateQueryData("getDiamondById", id, (draft) => {
              if (draft?.data) draft.data.isActive = false;
            })
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
      invalidatesTags: (_res, _err, id) => [{ type: "Diamond", id }],
    }),
  }),
});

export const {
  useAddDiamondMutation,
  useGetAllDiamondsQuery,
  useGetDiamondByIdQuery,
  useUpdateDiamondMutation,
  useDeleteDiamondMutation,
  useActivateDiamondMutation,
  useDeactivateDiamondMutation,
} = diamondApi;

export default diamondApi;
